import React from 'react';
import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import type { NavLink } from '@atlassian/jira-navigation-types/src/types.tsx';
import {
	type Action,
	createContainer,
	createHook,
	createStore,
} from '@atlassian/react-sweet-state';
import { getItemFromLink } from '../transformer/index.tsx';
import type { ShortcutItem } from '../types.tsx';
import type { ContainerProps, State } from './types.tsx';

const renderShortcutIcon = () => <ShortcutIcon spacing="spacious" label="shortcut" />;

/**
 * Actions
 * =======
 */

/**
 * Add item
 */
export const addItem =
	(item: ShortcutItem): Action<State> =>
	({ setState, getState }) => {
		const { shortcutType } = item;
		const state = getState();
		const index = state.data.map((i) => i.shortcutType).lastIndexOf(shortcutType);
		const newItem = {
			before: renderShortcutIcon,
			...item,
			target: '_blank',
			canEdit: true,
			itemType: 'shortcut',
		};

		// Append new item to same shortcuts group
		if (index === -1) {
			// @ts-expect-error - TS2322 - Type 'ShortcutItem | { target: string; canEdit: boolean; itemType: string; before: ComponentType<Record<any, any>> | (() => Element); href?: string | undefined; ... 7 more ...; text?: string | undefined; }' is not assignable to type 'ShortcutItem'. | TS2322 - Type '{ target: string; canEdit: boolean; itemType: string; before: React.ComponentType<Record<any, any>> | (() => JSX.Element); href?: string | undefined; icon?: string | undefined; ... 6 more ...; text?: string | undefined; }' is not assignable to type 'ShortcutItem'.
			setState({ data: [...state.data, newItem] });
		} else {
			setState({
				// @ts-expect-error - TS2322 - Type '(ShortcutItem | { target: string; canEdit: boolean; itemType: string; before: ComponentType<Record<any, any>> | (() => Element); href?: string | undefined; ... 7 more ...; text?: string | undefined; })[]' is not assignable to type 'ShortcutItem[]'.
				data: [...state.data.slice(0, index + 1), newItem, ...state.data.slice(index + 1)],
			});
		}
	};

/**
 * Delete item
 */
export const deleteItem =
	(id: string): Action<State> =>
	({ setState, getState }) => {
		const { data } = getState();
		const newItems = data.filter((existingItem) => existingItem.id !== id);

		setState({ data: newItems });
	};

/**
 * Edit item
 */
export const editItem =
	(item: ShortcutItem): Action<State> =>
	({ setState, getState }) => {
		const { data } = getState();
		const newItems = data.map((existingItem) =>
			existingItem.id !== item.id ? existingItem : item,
		);

		setState({ data: newItems });
	};

/**
 * Edit items
 */
export const editItems =
	(items: ShortcutItem[]): Action<State> =>
	({ setState, getState }) => {
		if (!items.length) {
			return;
		}

		const { data } = getState();
		const newItems = data.map(
			(existingItem) => items.find((item) => item.id === existingItem.id) || existingItem,
		);

		setState({ data: newItems });
	};

/**
 * Update container
 */
export const updateContainer =
	(data: NavLink[], loading: boolean): Action<State> =>
	({ setState, getState }) => {
		const items: ShortcutItem[] = (data || []).map(getItemFromLink);
		const state = getState();

		return setState({
			...state,
			loading,
			data: items,
			oldData: items,
			isLoaded: true,
		});
	};

export const actions = {
	addItem,
	deleteItem,
	editItem,
	editItems,
	updateContainer,
} as const;

export type Actions = typeof actions;

/**
 * Store
 * =======
 */

/**
 * Initial state
 */
export const initialState: State = {
	data: [],
	oldData: null,
	loading: false,
	isLoaded: false,
};

/**
 * Instance
 */
const Store = createStore<State, Actions>({
	name: 'shortcuts',
	initialState,
	actions,
});

export default Store;

export const ShortcutsContainer = createContainer<State, Actions, ContainerProps>(Store, {
	onInit:
		() =>
		({ dispatch }, { data, loading }: ContainerProps) => {
			dispatch(actions.updateContainer(data, loading));
		},
	onUpdate:
		() =>
		({ dispatch }, { data, loading }: ContainerProps) => {
			dispatch(actions.updateContainer(data, loading));
		},
});

export const useShortcutItems = createHook(Store);
